import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import SettingsProfile from '../components/settings/SettingsProfile';
import SettingsBilling from '../components/settings/SettingsBilling';
import SettingsUsage from '../components/settings/SettingsUsage';
import SettingsSubscription from '../components/settings/SettingsSubscription';
import SettingsFinances from '../components/settings/SettingsFinances';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const tabs = [
  { id: 'profile', labelKey: 'tabs.profile', icon: 'id-card' },
  { id: 'billing', labelKey: 'tabs.billing', icon: 'file-invoice-dollar' },
  { id: 'usage', labelKey: 'tabs.usage', icon: 'chart-pie' },
  { id: 'subscription', labelKey: 'tabs.subscription', icon: 'crown' },
  { id: 'finances', labelKey: 'tabs.finances', icon: 'receipt' },
];

const SettingsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { userProfile, profileLoading } = useAuth();
  const { t } = useTranslation('settings');
  
  // Get initial tab from URL or default to 'profile'
  const initialTab = searchParams.get('tab');
  const validTabs = tabs.map(t => t.id);
  const [activeTab, setActiveTab] = useState(
    validTabs.includes(initialTab) ? initialTab : 'profile'
  );

  // Sync URL when tab changes
  useEffect(() => {
    if (activeTab !== 'profile') {
      setSearchParams({ tab: activeTab }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [activeTab, setSearchParams]);

  const renderContent = () => {
    if (profileLoading && !userProfile) {
      return (
        <div className="flex items-center justify-center py-20 text-slate-500 dark:text-gray-400">
          <FontAwesomeIcon icon="circle-notch" spin className="mr-2" />
          {t('status.loadingProfile')}
        </div>
      );
    }
    if (!userProfile) {
      return (
        <div className="rounded-lg border border-red-500/60 bg-red-500/10 px-4 py-6 text-sm text-red-200">
          {t('status.noProfile')}
        </div>
      );
    }

    switch (activeTab) {
      case 'profile':
        return <SettingsProfile />;
      case 'billing':
        return <SettingsBilling />;
      case 'usage':
        return <SettingsUsage />;
      case 'subscription':
        return <SettingsSubscription />;
      case 'finances':
        return <SettingsFinances />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-gray-950 dark:text-white">
      <div className="mx-auto max-w-5xl px-4 py-8">
        <div className="mb-8">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{t('title')}</h1>
            <p className="text-sm text-slate-500 dark:text-gray-400">
              {t('subtitle')}
            </p>
        </div>

        {/* Top Navigation Bar */}
        <div className="mb-8 border-b border-slate-200 dark:border-gray-800">
          <nav className="-mb-px flex space-x-6 overflow-x-auto scrollbar-hide" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  group inline-flex items-center border-b-2 py-4 px-1 text-sm font-medium transition-colors whitespace-nowrap
                  ${
                    activeTab === tab.id
                      ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                      : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:text-gray-400 dark:hover:border-gray-700 dark:hover:text-gray-300'
                  }
                `}
              >
                <FontAwesomeIcon 
                  icon={tab.icon} 
                  className={`mr-2 ${
                    activeTab === tab.id ? 'text-indigo-500 dark:text-indigo-400' : 'text-slate-400 group-hover:text-slate-500 dark:text-gray-500 dark:group-hover:text-gray-400'
                  }`} 
                />
                {t(tab.labelKey)}
              </button>
            ))}
          </nav>
        </div>

        {/* Content Area */}
        <div className="min-h-[400px]">
            {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
